import React from 'react';
import {
  CalendarClock, CalendarCheck, CalendarX, Wallet, Heart, MessageSquareText,
  RefreshCw, Send, CheckCircle2, Clock, XCircle, AlertTriangle, PauseCircle, Loader2, Info,
  Key, Bell, Star, Gift, Phone, Zap, Link2, Link2Off, RotateCcw, Layers, Sparkles,
} from 'lucide-react';
import { ruleSummary } from './ruleSummary';

const ICONS = {
  'calendar-clock': CalendarClock,
  'calendar-check': CalendarCheck,
  'calendar-x': CalendarX,
  'calendar-sync': CalendarClock,
  wallet: Wallet,
  heart: Heart,
  key: Key,
  bell: Bell,
  star: Star,
  gift: Gift,
};

const STATUS_META = {
  APPROVED: { label: 'Freigegeben', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', Icon: CheckCircle2 },
  PENDING: { label: 'In Prüfung', cls: 'bg-amber-100 text-amber-700 border-amber-200', Icon: Clock },
  IN_REVIEW: { label: 'In Prüfung', cls: 'bg-amber-100 text-amber-700 border-amber-200', Icon: Clock },
  REJECTED: { label: 'Abgelehnt', cls: 'bg-red-100 text-red-700 border-red-200', Icon: XCircle },
  ERROR: { label: 'Fehler', cls: 'bg-red-100 text-red-700 border-red-200', Icon: AlertTriangle },
  PAUSED: { label: 'Pausiert', cls: 'bg-slate-100 text-slate-600 border-slate-200', Icon: PauseCircle },
  DISABLED: { label: 'Deaktiviert', cls: 'bg-slate-100 text-slate-600 border-slate-200', Icon: PauseCircle },
  NOT_SUBMITTED: { label: 'Nicht eingereicht', cls: 'bg-orange-50 text-orange-700 border-orange-200', Icon: Send },
};

const CAT_LABEL = { UTILITY: 'Service', MARKETING: 'Marketing', AUTHENTICATION: 'Authentifizierung' };

const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.NOT_SUBMITTED;
  const Icon = m.Icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${m.cls}`}>
      <Icon className="h-3 w-3" /> {m.label}
    </span>
  );
};

export function WhatsAppCatalogView({ data, onSubmit, onSubmitAll, onSubmitNewVersion, onAutocorrect, onSync, syncing, submitting, correcting, configHint, rules, onLink, linking }) {
  if (!data) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-500" data-testid="wa-catalog-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Vorlagen werden geladen …
      </div>
    );
  }
  const configured = data.waba_configured;
  const templates = data.templates || [];
  const showLinks = Array.isArray(rules);
  const linkedTemplates = templates.filter((t) => t.linked_rule_id);
  const rejectedTemplates = templates.filter((t) => t.overall_status === 'REJECTED');
  const ruleById = (id) => (rules || []).find((r) => r.id === id);
  const ruleName = (id) => ruleById(id)?.name || id;
  const reasonsOf = (t) => (t.languages || [])
    .filter((l) => l.status === 'REJECTED' && l.rejected_reason)
    .map((l) => `${l.label}: ${l.rejected_reason}`);
  const fmtDate = (iso) => {
    if (!iso) return '';
    try { return `am ${new Date(iso).toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' })}`; }
    catch { return ''; }
  };

  return (
    <div data-testid="wa-catalog">
      {/* Verbindungs-Übersicht: welche Nummer sendet was (nur zentrale Invicta-Ansicht) */}
      {data.scope === 'invicta' && (
      <div className="mb-4 grid gap-3 sm:grid-cols-2" data-testid="wa-connection-overview">
        <div className="rounded-xl border border-slate-200 bg-white p-3.5">
          <div className="mb-1 flex items-center gap-2">
            <Phone className="h-4 w-4 text-emerald-600" />
            <span className="text-sm font-semibold text-slate-900">Zentrale Nummer (Invicta)</span>
            <span className={`ml-auto rounded-full border px-2 py-0.5 text-[11px] font-semibold ${configured ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-amber-200 bg-amber-50 text-amber-700'}`}>
              {configured ? 'Konfiguriert' : 'Nicht konfiguriert'}
            </span>
          </div>
          <p className="text-xs text-slate-500">
            Fallback für alle Mandanten <b>ohne eigene WhatsApp-Nummer</b>. Diese {templates.length} Vorlagen werden über die zentrale Invicta-Nummer verschickt.
          </p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-3.5">
          <div className="mb-1 flex items-center gap-2">
            <Zap className="h-4 w-4 text-indigo-600" />
            <span className="text-sm font-semibold text-slate-900">Eigene Nummer des Mandanten</span>
          </div>
          <p className="text-xs text-slate-500">
            Mandanten mit eigener WhatsApp Business-Nummer senden automatisch über ihre eigene Nummer.
            Einrichtung im Mandanten-Bereich → <span className="font-medium">WhatsApp</span> (eigene Vorlagen dort separat einreichbar).
          </p>
        </div>
      </div>
      )}

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <MessageSquareText className="h-4 w-4 text-emerald-600" />
          <span>{templates.length} Vorlagen · Sprachen DE · FR · IT · EN</span>
        </div>
        <button
          onClick={onSync}
          disabled={!configured || syncing}
          data-testid="wa-catalog-sync"
          className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Status aktualisieren
        </button>
      </div>

      {!configured && (
        <div className="mb-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="wa-catalog-config-warning">
          <Info className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div>
            <b>WABA noch nicht vollständig konfiguriert.</b> Einreichen bei Meta ist erst möglich, sobald{' '}
            <span className="font-medium">Business-Account-ID</span> und <span className="font-medium">Access-Token</span> hinterlegt sind.
            {configHint && <div className="mt-0.5 text-amber-700">{configHint}</div>}
          </div>
        </div>
      )}

      {showLinks && rejectedTemplates.length > 0 && (
        <div className="mb-4 rounded-2xl border border-red-200 bg-red-50/60 p-4" data-testid="wa-catalog-rejected">
          <div className="mb-2.5 flex items-center gap-2 text-sm font-semibold text-red-800">
            <XCircle className="h-4 w-4" /> Von Meta abgelehnt ({rejectedTemplates.length})
          </div>
          <div className="space-y-2">
            {rejectedTemplates.map((t) => (
              <div key={t.key} className="rounded-lg bg-white/80 px-3 py-2.5" data-testid={`wa-catalog-rejected-row-${t.key}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-semibold text-slate-900">{t.title}</span>
                  {t.version > 1 && <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500">v{t.version}</span>}
                  <div className="ml-auto flex items-center gap-1.5">
                    <button
                      onClick={() => onAutocorrect && onAutocorrect(t.key)}
                      disabled={!!submitting || correcting === t.key}
                      data-testid={`wa-catalog-autocorrect-${t.key}`}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-violet-300 bg-violet-50 px-2.5 py-1.5 text-[11px] font-semibold text-violet-700 hover:bg-violet-100 disabled:opacity-50"
                      title="KI schlägt einen korrigierten Text vor"
                    >
                      {correcting === t.key ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} KI-Korrekturvorschlag
                    </button>
                    <button
                      onClick={() => onSubmitNewVersion && onSubmitNewVersion(t.key)}
                      disabled={!configured || !!submitting}
                      data-testid={`wa-catalog-resubmit-${t.key}`}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-2.5 py-1.5 text-[11px] font-semibold text-white hover:bg-red-700 disabled:opacity-50"
                      title="Als neue Version (v+1) erneut bei Meta einreichen"
                    >
                      {submitting === `${t.key}:all` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />} Neue Version einreichen
                    </button>
                  </div>
                </div>
                {reasonsOf(t).length > 0 ? (
                  <ul className="mt-1.5 space-y-0.5 text-[11px] text-red-700">
                    {reasonsOf(t).map((r, i) => (<li key={i}>• {r}</li>))}
                  </ul>
                ) : (
                  <div className="mt-1 text-[11px] text-red-600">Meta hat keinen Grund angegeben. Inhalt anpassen und als neue Version einreichen.</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {showLinks && linkedTemplates.length > 0 && (
        <div className="mb-4 rounded-2xl border border-indigo-100 bg-indigo-50/40 p-4" data-testid="wa-catalog-links-overview">
          <div className="mb-2.5 flex items-center gap-2 text-sm font-semibold text-indigo-800">
            <Link2 className="h-4 w-4" /> Aktive Zuordnungen ({linkedTemplates.length})
          </div>
          <div className="space-y-1.5">
            {linkedTemplates.map((t) => (
              <div key={t.key} className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg bg-white/70 px-3 py-2 text-xs" data-testid={`wa-catalog-link-row-${t.key}`}>
                <span className="font-semibold text-slate-900">{t.title}</span>
                <span className="text-slate-400">→</span>
                <span className="font-medium text-indigo-700">{ruleName(t.linked_rule_id)}</span>
                <span className="ml-auto flex items-center gap-2">
                  <StatusBadge status={t.overall_status} />
                  {t.link_applied ? (
                    <span className="inline-flex items-center gap-1 font-medium text-emerald-700">
                      <CheckCircle2 className="h-3 w-3" /> freigegeben {fmtDate(t.link_applied_at)}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 font-medium text-amber-700">
                      <Clock className="h-3 w-3" /> wartet auf Freigabe
                    </span>
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        {templates.map((t) => {
          const Icon = ICONS[t.icon] || MessageSquareText;
          const allBusy = submitting === `${t.key}:all`;
          return (
            <div key={t.key} className="flex flex-col rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={`wa-catalog-card-${t.key}`}>
              <div className="mb-2 flex items-start justify-between gap-2">
                <div className="flex items-center gap-2.5">
                  <div className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
                    <Icon className="h-4.5 w-4.5" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-slate-900">{t.title}</div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-[11px] font-medium text-slate-400">{CAT_LABEL[t.category] || t.category}</span>
                      <StatusBadge status={t.overall_status} />
                      {t.version > 1 && (
                        <span className="inline-flex items-center gap-0.5 rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500" data-testid={`wa-catalog-version-${t.key}`}>
                          <Layers className="h-2.5 w-2.5" /> v{t.version}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <button
                    onClick={() => onSubmitAll(t.key)}
                    disabled={!configured || !!submitting}
                    data-testid={`wa-catalog-submit-all-${t.key}`}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-2.5 py-1.5 text-[11px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                    title="Alle Sprachen zur Meta-Prüfung einreichen"
                  >
                    {allBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Alle einreichen
                  </button>
                  {t.overall_status !== 'NOT_SUBMITTED' && (
                    <button
                      onClick={() => onSubmitNewVersion(t.key)}
                      disabled={!configured || !!submitting}
                      data-testid={`wa-catalog-newversion-${t.key}`}
                      className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-2 py-1 text-[10px] font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                      title="Neue Version (v+1) einreichen — nötig, wenn Meta den Namen sperrt"
                    >
                      <RotateCcw className="h-3 w-3" /> Neue Version
                    </button>
                  )}
                </div>
              </div>

              <p className="mb-2 text-xs text-slate-500">{t.description}</p>

              {t.trigger && (
                <div className="mb-2 inline-flex items-center gap-1.5 rounded-md bg-slate-50 px-2 py-1 text-[11px] text-slate-500" data-testid={`wa-catalog-trigger-${t.key}`}>
                  <Zap className="h-3 w-3 text-slate-400" /> {t.trigger}
                </div>
              )}

              {showLinks && (
                <div className="mb-3 rounded-lg border border-indigo-100 bg-indigo-50/50 p-2.5" data-testid={`wa-catalog-link-${t.key}`}>
                  <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold text-indigo-700">
                    <Link2 className="h-3.5 w-3.5" /> Automatische Zuordnung
                  </div>
                  <div className="flex items-center gap-2">
                    <select
                      value={t.linked_rule_id || ''}
                      onChange={(e) => onLink && onLink(t.key, e.target.value)}
                      disabled={linking === t.key}
                      data-testid={`wa-catalog-link-select-${t.key}`}
                      className="min-w-0 flex-1 rounded-md border border-indigo-200 bg-white px-2 py-1.5 text-xs text-slate-700 focus:border-indigo-400 focus:outline-none disabled:opacity-50"
                    >
                      <option value="">— keine Zuordnung —</option>
                      {(rules || []).map((r) => (
                        <option key={r.id} value={r.id}>{r.name || r.id}</option>
                      ))}
                    </select>
                    {linking === t.key && <Loader2 className="h-3.5 w-3.5 animate-spin text-indigo-500" />}
                  </div>
                  {t.linked_rule_id && ruleById(t.linked_rule_id) && (
                    <div className="mt-1.5 flex items-center gap-1 text-[11px] text-indigo-600" data-testid={`wa-catalog-rule-summary-${t.key}`}>
                      <Zap className="h-3 w-3" /> Löst aus: {ruleSummary(ruleById(t.linked_rule_id))}
                    </div>
                  )}
                  {t.linked_rule_id && (
                    t.link_applied ? (
                      <div className="mt-1.5 flex items-center gap-1 text-[11px] font-medium text-emerald-700" data-testid={`wa-catalog-link-applied-${t.key}`}>
                        <CheckCircle2 className="h-3 w-3" /> Zugeordnet — Vorlage ist freigegeben und aktiv
                      </div>
                    ) : (
                      <div className="mt-1.5 flex items-center gap-1 text-[11px] font-medium text-amber-700" data-testid={`wa-catalog-link-pending-${t.key}`}>
                        <Clock className="h-3 w-3" /> Wird automatisch zugeordnet, sobald Meta die Vorlage freigibt
                      </div>
                    )
                  )}
                  {!t.linked_rule_id && (
                    <div className="mt-1.5 flex items-center gap-1 text-[11px] text-slate-400">
                      <Link2Off className="h-3 w-3" /> Noch keiner Automatisierung zugeordnet
                    </div>
                  )}
                </div>
              )}

              <div className="mb-3 rounded-lg bg-slate-50 p-2.5 text-xs text-slate-600">
                {t.languages?.[0]?.header && <div className="mb-1 font-semibold text-slate-700">{t.languages[0].header}</div>}
                <div className="whitespace-pre-wrap">{t.languages?.[0]?.preview}</div>
                {t.languages?.[0]?.footer && <div className="mt-1 text-[11px] text-slate-400">{t.languages[0].footer}</div>}
                {t.button && <div className="mt-2 inline-block rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium text-emerald-700">{t.button}</div>}
              </div>

              <div className="mt-auto space-y-1.5">
                {(t.languages || []).map((l) => {
                  const busy = submitting === `${t.key}:${l.lang}`;
                  return (
                    <div key={l.lang} className="flex items-center justify-between gap-2 rounded-lg border border-slate-100 px-2.5 py-1.5" data-testid={`wa-catalog-lang-${t.key}-${l.lang}`}>
                      <span className="w-20 text-xs font-medium text-slate-600">{l.label}</span>
                      <div className="flex items-center gap-2">
                        <StatusBadge status={l.status} />
                        <button
                          onClick={() => onSubmit(t.key, l.lang)}
                          disabled={!configured || !!submitting}
                          data-testid={`wa-catalog-submit-${t.key}-${l.lang}`}
                          className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                        >
                          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                          {l.status === 'NOT_SUBMITTED' ? 'Einreichen' : 'Erneut'}
                        </button>
                      </div>
                    </div>
                  );
                })}
                {(t.languages || []).some((l) => l.status === 'REJECTED' && l.rejected_reason) && (
                  <div className="pt-1 text-[11px] text-red-600">
                    {(t.languages || []).filter((l) => l.status === 'REJECTED' && l.rejected_reason).map((l) => (
                      <div key={l.lang}>{l.label}: {l.rejected_reason}</div>
                    ))}
                  </div>
                )}
                {(t.languages || []).some((l) => l.status === 'ERROR' && l.error) && (
                  <div className="pt-1 text-[11px] text-red-600">
                    {(t.languages || []).filter((l) => l.status === 'ERROR' && l.error).map((l) => (
                      <div key={l.lang} className="truncate" title={l.error}>{l.label}: {l.error}</div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
